import { toast } from "sonner";
import {
  GitBranch,
  Truck,
  Boxes,
  FileText,
  AlertTriangle,
  FileDown,
  Package,
  Factory,
} from "lucide-react";
import { trace } from "@/data/fixtures";
import SourceChip from "@/components/SourceChip";

const kindIcon = {
  "supplier-lot": Truck,
  "work-order": Factory,
  "finished-lot": Boxes,
  "sales-order": Package,
};

const edgeLabel = {
  "consumed-in": "consumed in",
  "produced-by": "produced",
  "shipped-on": "shipped on",
};

export default function Genealogy() {
  const onExport = () =>
    toast.success("Containment scope report exported · CSV", { duration: 4000 });

  const isIncomplete = trace.completeness === "incomplete";

  return (
    <div data-testid="page-genealogy" className="space-y-6">
      {/* Persistent SP-05 statement */}
      <div
        className="rounded-md border px-4 py-2.5 text-[12.5px]"
        style={{
          background: "#F1F5F9",
          borderColor: "var(--mos-border)",
          color: "var(--mos-text-muted)",
        }}
        data-testid="genealogy-banner"
      >
        <span className="font-medium text-slate-700">Note · </span>
        {trace.banner}
      </div>

      {/* Header */}
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <div className="text-[11px] tracking-[0.22em] uppercase text-slate-500 mb-2 flex items-center gap-2">
            <GitBranch size={12} />
            Containment Scope Report
          </div>
          <h1 className="text-[30px] leading-tight font-semibold text-slate-900 tracking-tight">
            Trace seeded on{" "}
            <span className="mos-mono">{trace.seedNode.label}</span>
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-3 text-[13px] text-slate-600">
            <span>
              Run{" "}
              <span className="mos-mono text-slate-900">{trace.runAt}</span> by{" "}
              <span className="text-slate-900">{trace.runBy}</span>
            </span>
            <SourceChip
              system={trace.seedNode.source.system}
              record={trace.seedNode.source.record}
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <span
            className="text-[11px] font-semibold tracking-wider uppercase px-2 py-1 rounded border"
            style={
              isIncomplete
                ? {
                    color: "var(--mos-amber)",
                    background: "var(--mos-amber-bg)",
                    borderColor: "var(--mos-amber-border)",
                  }
                : {
                    color: "var(--mos-green)",
                    background: "var(--mos-green-bg)",
                    borderColor: "var(--mos-green-border)",
                  }
            }
            data-testid="genealogy-completeness"
          >
            {isIncomplete ? "Report incomplete · declared breaks" : "Report complete"}
          </span>
          <button
            type="button"
            onClick={onExport}
            className="mos-btn mos-btn-secondary"
            data-testid="btn-export-trace"
          >
            <FileDown size={14} />
            Export
          </button>
        </div>
      </div>

      {/* Scope summary */}
      <div className="grid grid-cols-4 gap-4">
        <ScopeCard label="Sales orders touched" value={trace.scope.salesOrdersAffected} />
        <ScopeCard label="Units already shipped" value={trace.scope.unitsShippedAlready} tone="amber" />
        <ScopeCard label="Units pending" value={trace.scope.unitsPending} />
        <ScopeCard label="Work orders affected" value={trace.scope.workOrdersAffected} />
      </div>

      {/* Declared breaks banner */}
      {trace.breaks.map((b) => (
        <div
          key={b.id}
          className="rounded-md p-4 border-l-4 flex items-start gap-3"
          style={{
            background: "var(--mos-amber-bg)",
            borderLeftColor: "var(--mos-amber)",
            borderColor: "var(--mos-amber-border)",
            borderWidth: "1px",
            borderStyle: "solid",
          }}
          data-testid={`trace-break-${b.id}`}
        >
          <AlertTriangle size={16} style={{ color: "var(--mos-amber)" }} className="flex-none mt-0.5" />
          <div>
            <div className="text-[13.5px] font-semibold text-[color:var(--mos-amber)]">
              {b.label}
            </div>
            <div className="text-[13px] text-slate-700 mt-1 leading-relaxed">
              {b.detail}
            </div>
          </div>
        </div>
      ))}

      {/* Graph traversal · rendered as a tree */}
      <div className="mos-surface rounded-md p-6" data-testid="trace-graph">
        <div className="text-[11px] tracking-[0.18em] uppercase text-slate-500 font-semibold mb-4">
          Traversal · downstream from seed
        </div>
        <div className="space-y-1">
          {trace.nodes.map((n) => {
            const Icon = kindIcon[n.kind] || FileText;
            const isSeed = n.depth === 0;
            const brokenAfter = trace.breaks.some((b) => b.betweenNodes[0] === n.id);
            return (
              <div
                key={n.id}
                className="flex items-start"
                style={{ paddingLeft: `${n.depth * 32}px` }}
                data-testid={`trace-node-${n.id}`}
              >
                {n.depth > 0 && (
                  <span
                    className="text-slate-300 mos-mono text-[12px] mr-3 mt-2 flex-none"
                    style={{ minWidth: "68px" }}
                  >
                    └─ {edgeLabel[n.edge]}
                  </span>
                )}
                <div className="flex-1 py-2">
                  <div className="flex items-center gap-3">
                    <span
                      className="h-7 w-7 rounded-full inline-flex items-center justify-center flex-none"
                      style={{
                        background: isSeed ? "var(--mos-accent)" : "#EFF3F9",
                      }}
                    >
                      <Icon
                        size={13}
                        style={{
                          color: isSeed ? "#fff" : "var(--mos-accent)",
                        }}
                      />
                    </span>
                    <span className="mos-mono text-[14px] font-medium text-slate-900">
                      {n.label}
                    </span>
                    {n.shippedStatus === "shipped" && (
                      <span
                        className="text-[10.5px] font-semibold tracking-wider uppercase px-1.5 py-0.5 rounded border"
                        style={{
                          color: "var(--mos-amber)",
                          background: "var(--mos-amber-bg)",
                          borderColor: "var(--mos-amber-border)",
                        }}
                      >
                        already shipped
                      </span>
                    )}
                    {n.shippedStatus === "pending" && (
                      <span
                        className="text-[10.5px] font-semibold tracking-wider uppercase px-1.5 py-0.5 rounded border"
                        style={{
                          color: "var(--mos-text-muted)",
                          background: "#F1F5F9",
                          borderColor: "var(--mos-border)",
                        }}
                      >
                        pending
                      </span>
                    )}
                  </div>
                  <div className="mt-1 ml-10 text-[12.5px] text-slate-600">
                    {n.detail}
                  </div>
                  <div className="mt-1.5 ml-10 flex items-center gap-2 flex-wrap">
                    <SourceChip system={n.source.system} record={n.source.record} />
                    {n.edgeSource && (
                      <span className="text-[11px] text-slate-400">
                        edge · <span className="mos-mono">{n.edgeSource}</span>
                      </span>
                    )}
                  </div>
                  {brokenAfter && (
                    <div
                      className="mt-2 ml-10 text-[12px] font-medium flex items-center gap-2"
                      style={{ color: "var(--mos-amber)" }}
                    >
                      <AlertTriangle size={12} />
                      Traceability break declared beyond this node.
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <p className="text-[11.5px] text-slate-500">
        Trace access is role-restricted to quality, regulatory, and executive roles. This
        query has been logged to the audit trail (SP-03).
      </p>
    </div>
  );
}

function ScopeCard({ label, value, tone }) {
  const color = tone === "amber" ? "var(--mos-amber)" : "var(--mos-text)";
  return (
    <div
      className="mos-surface rounded-md p-4"
      data-testid={`scope-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="text-[11px] uppercase tracking-wider text-slate-500">
        {label}
      </div>
      <div
        className="mos-mono text-[22px] font-semibold mt-1"
        style={{ color }}
      >
        {value}
      </div>
    </div>
  );
}
